@if (needChooseService()) {
  <beautyapp-choose-service
    [employeeId]="employee()?.id"
    [roomId]="room()?.id"
    [userId]="userId()"
    [bookingDate]="bookingDate()"
    (serviceSelected)="chooseService($event)"
    (packageServiceSelected)="choosePackageService($event)"
  />
} @else {
  <form [formGroup]="form" class="flex flex-col gap-5">
    <!-- ══ Zone: Procedure & pricing ══ -->
    <section class="flex flex-col gap-3">
      <div class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
        <span>{{ 'calendar.form.procedureAndPrice' | translate }}</span>
        <span class="flex-1 h-px bg-gray-200"></span>
      </div>

      <div class="flex flex-col xl:flex-row gap-4 xl:items-start">
        <!-- Selected procedure -->
        <div class="flex flex-col gap-1">
          <div class="text-xs flex items-center h-[24px]">
            {{ 'calendar.form.procedure' | translate }}
          </div>
          <div class="flex gap-2 justify-between items-center border border-primary-400 rounded-[7px] px-4 py-2 w-96 min-h-[40px]">
            <div class="flex items-center gap-2">
              {{ service()?.name }}
              @if (initialData()?.offerPurchaseId || service()?.offerPurchaseId) {
                <nz-tag nzColor="gold" class="text-xs">
                  <i nz-icon nzType="gift" nzTheme="outline"></i>
                  {{ 'calendar.offerService' | translate }}
                </nz-tag>
              }
              @if (service()?.packagePurchaseId) {
                <nz-tag nzColor="cyan" class="text-xs">
                  <i nz-icon nzType="gift" nzTheme="outline"></i>
                  {{ 'calendar.packageService' | translate }}
                </nz-tag>
              }
            </div>
            @if (!viewOnly()) {
              <div>
                <button
                  nz-button
                  nzType="primary"
                  nzSize="small"
                  type="button"
                  (click)="needChooseService.set(true)"
                >
                  <i nz-icon nzType="edit" nzTheme="outline"></i>
                </button>
              </div>
            }
          </div>
        </div>

        <div class="flex gap-3 flex-1">
          <!-- Duration -->
          <div class="flex flex-col gap-3 flex-initial w-[100px] duration-field">
            <beautyapp-input
              label="{{ 'calendar.form.duration' | translate }}"
              placeholder="{{ 'calendar.form.time' | translate }}"
              formControlName="time"
              mask="00:00"
              required
              size="large"
              [readonly]="viewOnly()"
            ></beautyapp-input>
          </div>

          @if (service()?.serviceType === 'STANDARD') {
            <!-- Price -->
            <div class="flex flex-col gap-3 flex-1">
              <beautyapp-input
                label="{{ 'calendar.form.price' | translate }}"
                placeholder="{{ 'calendar.form.gel' | translate }}"
                type="number"
                prefixIcon="dollar"
                formControlName="price"
                required
                size="large"
                [readonly]="viewOnly()"
              ></beautyapp-input>
            </div>
            <!-- Discount % -->
            <div class="flex flex-col gap-3 flex-1">
              <beautyapp-input
                label="{{ 'calendar.form.discount' | translate }}"
                placeholder="{{ 'calendar.form.percent' | translate }}"
                prefixIcon="percentage"
                type="number"
                formControlName="salePercent"
                size="large"
                [min]="0"
                [max]="100"
                [readonly]="viewOnly()"
                (ngModelChange)="changePercentage($event)"
              ></beautyapp-input>
            </div>
            <!-- Discounted Price -->
            <div class="flex flex-col gap-3 flex-1">
              <beautyapp-input
                label="{{ 'calendar.form.discountedPrice' | translate }}"
                placeholder="{{ 'calendar.form.gel' | translate }}"
                prefixIcon="dollar"
                type="number"
                formControlName="salePrice"
                size="large"
                [readonly]="viewOnly()"
              ></beautyapp-input>
            </div>
          } @else if (service()?.serviceType === 'MINUTE') {
            <!-- Minute-based pricing display -->
            <div class="flex flex-col gap-1 h-[40px] self-end">
              <div class="flex gap-1 items-center h-[20px]">
                <div class="flex items-center">წუთის ფასი:</div>
                @if (+service()?.discountPrice > 0) {
                  <div class="flex items-center gap-1">
                    <div>{{ service()?.discountPrice }} <i class="lari lari-c"></i></div>
                    <del>{{ service()?.price }} <i class="lari lari-c"></i></del>
                  </div>
                }
                @if (+service()?.discountPrice <= 0) {
                  <div>{{ service()?.price }} <i class="lari lari-c"></i></div>
                }
              </div>

              <div class="flex gap-1 items-center h-[20px]">
                <div class="flex items-center">ფასი:</div>
                @if (+(form.get('salePrice')?.value || 0) > 0) {
                  <div class="flex items-center gap-1">
                    <div>{{ form.get('salePrice')?.value }} <i class="lari lari-c"></i></div>
                    <del>{{ form.get('price')?.value }} <i class="lari lari-c"></i></del>
                  </div>
                }
                @if (+(form.get('salePrice')?.value || 0) <= 0) {
                  <div>{{ form.get('price')?.value }} <i class="lari lari-c"></i></div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- ══ Zone: Staff (main + additional as one table, headers once) ══ -->
    <section class="flex flex-col gap-2">
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400 flex-1">
          <span>{{ 'calendar.form.employees' | translate }}</span>
          <span class="flex-1 h-px bg-gray-200"></span>
        </div>
        @if (roomAllowsMultipleEmployees() && !viewOnly()) {
          <button
            nz-button
            nzType="primary"
            nzSize="small"
            type="button"
            (click)="addAdditionalEmployee()"
          >
            <i nz-icon nzType="plus" nzTheme="outline"></i>
            {{ 'calendar.form.addEmployee' | translate }}
          </button>
        }
      </div>

      <!-- Column header (once) -->
      <div class="flex gap-2 text-xs text-gray-400 px-1">
        <div class="flex-1">{{ 'calendar.form.employee' | translate }}</div>
        <ng-container *ngxPermissionsOnly="['field.employee_salary']">
          <div class="flex-1">{{ 'calendar.form.fixatedSalary' | translate }}</div>
          <div class="flex-1">{{ 'calendar.form.salary%' | translate }}</div>
        </ng-container>
        <div class="w-[52px]"></div>
      </div>

      <!-- Main employee (read-only) -->
      <div class="flex gap-2 items-start">
        <div class="flex-1">
          <div class="flex items-center border border-gray-300 rounded-[7px] px-4 min-h-[40px] bg-gray-50">
            {{ employee()?.user?.fullName || '-' }}
          </div>
        </div>
        <ng-container *ngxPermissionsOnly="['field.employee_salary']">
          <div class="flex-1">
            <beautyapp-input
              placeholder="{{ 'calendar.form.fixatedSalary' | translate }}"
              type="number"
              prefixIcon="dollar"
              formControlName="salaryFixed"
              [readonly]="salaryReadonly() || viewOnly()"
              size="large"
            ></beautyapp-input>
          </div>
          <div class="flex-1">
            <!--
              beaut-424: [min]/[max] put the constraint on the native input so
              the browser's own stepper cannot exceed it, and [errors] maps the
              Validators.min/max failures onto a readable message — the shared
              input falls back to this map for any error key it does not handle
              itself.
            -->
            <beautyapp-input
              placeholder="{{ 'calendar.form.salary%' | translate }}"
              prefixIcon="percentage"
              type="number"
              formControlName="salaryPercent"
              [min]="0"
              [max]="100"
              [errors]="{ min: 'validators.percentRange', max: 'validators.percentRange' }"
              [readonly]="salaryReadonly() || viewOnly()"
              size="large"
            ></beautyapp-input>
            @if (isEnableSalaryPercentFromPrice && !viewOnly()) {
              <label nz-checkbox formControlName="salaryPercentFromPrice" class="mt-1">
                {{ 'calendar.form.fromValue' | translate }}
              </label>
            }
          </div>
        </ng-container>
        <!-- main employee is fixed (from the booking) — no remove -->
        <div class="w-[52px]"></div>
      </div>

      <!-- Additional employees (only when the chosen room allows multiple) -->
      @if (roomAllowsMultipleEmployees()) {
        <div class="flex flex-col gap-2" formArrayName="additionalEmployees">
          @for (employee of additionalEmployeesForm.controls; track $index; let idx = $index) {
            <div class="flex gap-2 items-start" [formGroupName]="idx">
              <div class="flex-1">
                <nz-select
                  [nzPlaceHolder]="'calendar.form.selectEmployee' | translate"
                  nzSize="large"
                  formControlName="employeeId"
                  [nzDisabled]="viewOnly()"
                  (ngModelChange)="onAdditionalEmployeeChange(idx, $event)"
                  class="w-full"
                >
                  @for (emp of employees(); track emp.id) {
                    <nz-option [nzValue]="emp.id" [nzLabel]="emp.fullName"></nz-option>
                  }
                </nz-select>
              </div>

              <ng-container *ngxPermissionsOnly="['field.employee_salary']">
                <div class="flex-1">
                  <nz-input-number
                    [nzPlaceHolder]="'calendar.form.fixatedSalary' | translate"
                    nzSize="large"
                    formControlName="salaryFixed"
                    [nzDisabled]="salaryReadonly() || viewOnly()"
                    class="w-full"
                  ></nz-input-number>
                </div>

                <div class="flex-1">
                  <!-- beaut-424: additional employees split the same price, so
                       each share carries the same 0-100 ceiling. nz-input-number
                       clamps on blur, which the plain input does not. -->
                  <nz-input-number
                    [nzPlaceHolder]="'calendar.form.salary%' | translate"
                    nzSize="large"
                    formControlName="salaryPercent"
                    [nzMin]="0"
                    [nzMax]="100"
                    [nzDisabled]="salaryReadonly() || viewOnly()"
                    class="w-full"
                  ></nz-input-number>
                </div>
              </ng-container>

              <div class="w-[52px] flex justify-center">
                @if (!viewOnly()) {
                  <button
                    nz-button
                    nzType="default"
                    nzDanger
                    nzSize="large"
                    type="button"
                    (click)="removeAdditionalEmployee(idx)"
                  >
                    <i nz-icon nzType="delete" nzTheme="outline"></i>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }
    </section>

    <!-- Products -->
    <beautyapp-appointment-products
      [serviceId]="service()?.id"
      [serviceProducts]="serviceProducts()"
      [bookingStatus]="bookingStatus()"
      [viewOnly]="viewOnly()"
      (serviceProductsUpdate)="serviceProductsUpdate($event)"
    ></beautyapp-appointment-products>

    <!-- ══ Zone: Expense (product cost + additional cost) ══ -->
    <section class="flex flex-col gap-3">
      <div class="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
        <span>{{ 'calendar.form.expense' | translate }}</span>
        <span class="flex-1 h-px bg-gray-200"></span>
      </div>

      <div class="flex gap-3">
        <!-- Product cost — seeded from the service products, editable so the
             operator can correct what a procedure actually consumed -->
        <div class="flex flex-col gap-3 flex-1">
          <beautyapp-input
            label="{{ 'booking.productExpense' | translate }}"
            placeholder="{{ 'calendar.form.gel' | translate }}"
            type="number"
            prefixIcon="dollar"
            formControlName="expense"
            size="large"
            [readonly]="viewOnly()"
          ></beautyapp-input>
        </div>

        <!-- Additional cost — editable, sent to the backend unchanged -->
        <div class="flex flex-col gap-3 flex-1">
          <beautyapp-input
            label="{{ 'booking.additionalExpense' | translate }}"
            placeholder="{{ 'calendar.form.gel' | translate }}"
            type="number"
            prefixIcon="dollar"
            formControlName="additionalExpense"
            size="large"
            [readonly]="viewOnly()"
          ></beautyapp-input>
        </div>

        <!-- Total = product cost + additional cost -->
        <div class="flex flex-col gap-1 flex-1">
          <div class="text-xs flex items-center h-[24px]">
            {{ 'booking.totalExpense' | translate }}
          </div>
          <div class="flex items-center gap-1 border border-gray-300 rounded-[7px] px-4 min-h-[40px] bg-gray-50">
            {{ totalExpense }} <i class="lari lari-c"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- Spec Services -->
    <beautyapp-appointment-spec-services
      [serviceId]="service()?.id"
      [serviceSpecs]="serviceSpecs()"
      [bookingStatus]="bookingStatus()"
      [viewOnly]="viewOnly()"
      (serviceSpecsUpdate)="serviceSpecsUpdate($event)"
    ></beautyapp-appointment-spec-services>
  </form>
}
